import React from 'react'
import { Card, Button, TextField } from '@therms/atalaya'
import { Formik } from 'formik'
import { PaperAirplaneIcon } from '@heroicons/react/24/solid'
import { UserName } from '@src/modules/Users/shared/UserName'
import { UserPosition } from '@src/modules/Messages/shared/UserPosition'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {GetThreadMessages200} from '@src/generatedClient/generated/taskreportsApi'


interface SiteUser {
  id: string | number;
  name?: string;
  position?: string;
}

interface ChatPanelProps {
  isSelectingUser: boolean;
  selectedUser?: SiteUser;
  threadMessages?: GetThreadMessages200;
  currentSiteUser: SiteUser;
  textInput: string;
  setTextInput: (value: string) => void;
  handleSendMessage: (values: any, helpers: { resetForm: any }) => Promise<void>;
}

dayjs.extend(relativeTime)

function ChatPanel({
  isSelectingUser,
  selectedUser,
  threadMessages,
  currentSiteUser,
  textInput,
  setTextInput,
  handleSendMessage
}: ChatPanelProps) {
  let content;
  if (isSelectingUser && !selectedUser) {
    content = (
      <div className="px-0 py-1 border-2 border-gray-200 rounded-xl flex justify-center items-center h-full">
        <div className="flex flex-col gap-3 px-4 py-3 bg-white text-center items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
          <p className="text-gray-400">Select a user to start a conversation</p>
        </div>
      </div>
    );
  } else if (selectedUser || (threadMessages?.messages && threadMessages?.messages.length > 0)) {
    content = (
      <Card className="px-0 border-gray-200 rounded-xl flex flex-col h-full">
        <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200"> 
          {(() => {
            let lastMessageUserId = null
            if (threadMessages?.messages?.length) {
              lastMessageUserId = threadMessages.messages[threadMessages.messages.length - 1].userId
            } else if (selectedUser) {
              lastMessageUserId = selectedUser.id
            }
            return (
              <>
                <div className="font-semibold text-lg">{lastMessageUserId ? <UserName id={lastMessageUserId} /> : selectedUser?.name}</div>
                <div className="flex items-center bg-gray-100 px-4 py-1 rounded-md text-sm">
                  {lastMessageUserId ? <UserPosition userId={lastMessageUserId} /> : selectedUser?.position}
                </div>
              </>
            )
          })()}
        </div>
        <div className="flex flex-col-reverse gap-4 px-4 py-3 h-full bg-white border-2 border-gray-100 overflow-y-auto">
          {threadMessages?.messages && threadMessages?.messages.length > 0 ? (
            threadMessages.messages.map((msg) => {
              let bubbleClass = ''
              if (msg.userId === currentSiteUser.id) {
                bubbleClass = 'bg-blue-500 text-white rounded-tl-xl rounded-br-xl'
              } else if (msg.isAdmin) {
                bubbleClass = 'bg-yellow-500 text-white rounded-bl-xl rounded-tr-xl'
              } else {
                bubbleClass = 'bg-gray-200 text-gray-800 rounded-bl-xl rounded-tr-xl'
              }
              return (
                <div
                  key={msg.id}
                  className={`flex flex-col gap-1 w-2/3 ${msg.userId === currentSiteUser.id ? 'self-end items-end' : 'self-start items-start'}`}
                >
                  <div className={`text-sm px-3 py-2  ${bubbleClass}`}>{msg.text}</div>
                  <div className="text-xs text-gray-400">
                    {dayjs(msg.cts).fromNow()} - {msg.userId === currentSiteUser.id ? 'You' : <UserName id={msg?.userId} />} - {msg.isAdmin ? 'Admin' : 'User'}
                  </div>
                </div>
              )
            })
          ) : (
            <div className="flex flex-col px-4 py-3 bg-white text-center items-center w-full justify-center h-full">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mb-1 " fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              <p className="text-gray-400">Start a conversation with <span className="font-semibold text-blue-600">{selectedUser?.name}</span></p>
            </div>
          )}
        </div>
        <Formik
          enableReinitialize
          initialValues={{ text: textInput }}
          onSubmit={handleSendMessage}
        >
          {({ handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <div className="flex px-2 py-2 border-t border-gray-200 gap-2">
                <div className="flex-grow">
                  <TextField
                    value={textInput}
                    onChangeValue={setTextInput}
                    placeholder="Type a message..."
                  />
                </div>
                <Button type="submit" className="w-10" variant="main">
                  <PaperAirplaneIcon className="w-5 h-5" />
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </Card>
    );
  } else {
    content = (
      <div className="px-0 py-1 border-2 border-gray-200 rounded-xl flex justify-center items-center h-full">
        <div className="flex flex-col gap-3 px-4 py-3 bg-white text-center w-full items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 mb-1 " fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
          <p className="text-gray-400">Select a conversation to start chatting</p>
        </div>
      </div>
    );
  }
  return <div className="sm:w-full md:w-7/12 max-h-[90vh] overflow-y-auto">{content}</div>;
}


export default ChatPanel
